// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import type { Skin } from '../skin';
import { classicSkin } from './classic';

/**
 * Clubhouse: the look of the website (website/public/css/goffy.css, after the Figma file
 * "Goffy — Mer spel i varje rond"). A cool mist ground under white panels, cream and sand
 * as the quiet surfaces, near-black forest ink for text and for the filled buttons, forest
 * green as the second accent and pale lime for highlights. Archivo ExtraBold headlines.
 *
 * The site's own accent is its ink, so accent.primary is the ink too: a selected pill or a
 * primary button is an ink fill with cream text, as on the site. The site's lime is a fill
 * only (the surface.tint family) — it is far too pale to be read as ink on this ground.
 * The hole map keeps classic's fills: the white labels and markers drawn over it need a dark
 * ground, and the site's pastel hole illustration would wash them out.
 */
export const clubhouseSkin: Skin = {
  colors: {
    background: {
      app: '#E2EAEA',
      panel: '#FFFFFF',
      panelAlt: '#F4F1EA',
      elevated: '#E6E4DC',
      deep: '#E9E7DF',
      interactive: '#1F2D24',
      interactiveMuted: '#D3D8D4',
    },
    surface: {
      base: '#FFFFFF',
      alt: '#F4F1EA',
      elevated: '#E6E4DC',
      input: '#FFFFFF',
      muted: '#DEDBD1',
      card: '#F8F6F1',
      light: '#FFFFFF',
      toggleOff: '#B7BDB8',
      // The site's lime, from its palest wash to its hover shade.
      tint: '#E6EFBF',
      tintMid: '#DBE8A0',
      tintDeep: '#CFE08A',
      // No brown on the site: the warm surfaces take its bunker sand instead.
      brown: '#DCD0B0',
      brownLight: '#E9E1CB',
      brownDeep: '#CFC19C',
      // Both a dropdown fill under ink and the scorecard title's ink on white: this tone keeps
      // the first at 4.5:1 and the second at 3:1 (large text).
      brownDark: '#A38F64',
    },
    text: {
      primary: '#1F2D24',
      secondary: '#56625B',
      muted: '#646F68',
      heading: '#1F2D24',
      inverse: '#F4F1EA',
      hint: '#5B6760',
      subtle: '#56625B',
      faint: '#86908A',
      dim: '#9AA39D',
      accentMuted: '#3D6A4B',
      // Dark enough to read on surface.tintMid, the site's lime.
      accentSoft: '#35603F',
      // Also set on border.strong as a fill (the friends screen), hence this dark.
      positive: '#23552F',
      warm: '#7A5A2E',
      onLight: '#1F2D24',
      slateMuted: '#6F7C88',
      // Ink that flips with the skin, by role: placeholder text, a label on a strong fill.
      placeholder: '#7C8680',
      onStrong: '#FFFFFF',
      // Ink over photos, video and the map: white in every skin, on purpose.
      onMedia: '#FFFFFF',
    },
    border: {
      // The site's --g-line.
      default: 'rgba(31,45,36,0.12)',
      strong: '#B8BEB7',
      // The site's forest green, as its focus ring: an ink outline reads heavier than its thin rules.
      accent: '#3D6A4B',
      accentMuted: '#C5CEC6',
      onLight: '#D8DCD6',
      brown: '#A67C2E',
      // Low-contrast rules on panels; they flip with the skin, unlike a white at low alpha.
      hairline: 'rgba(31,45,36,0.12)',
      subtle: 'rgba(31,45,36,0.24)',
    },
    neutral: {
      white: '#FFFFFF',
      black: '#000000',
      slate: '#E2E8F0',
      navy: '#111827',
      navyDeep: '#0C1435',
      ink: '#040711',
      grey: '#6B7280',
      greyDark: '#5B5B5B',
      charcoal: '#2C2C2C',
    },
    accent: {
      primary: '#1F2D24',
      positive: '#3D6A4B',
      secondary: '#3D6A4B',
      // light, soft, lime, limeSoft and mint are ink in almost every style that names them, so
      // on this ground they are readable greens (4.5:1 on the mist), not the site's pale lime.
      light: '#436F33',
      soft: '#436C50',
      lime: '#53701E',
      teal: '#3F7A74',
      tealDeep: '#2A5551',
      indigo: '#4C5A7E',
      glow: '#3D6A4B',
      subtleBg: '#EEF3D6',
      subtleBorder: 'rgba(31,45,36,0.25)',
      overlay: 'rgba(31,45,36,0.45)',
      // Darker than yardage-book's: the BETA badge sets it on its own wash on every screen.
      gold: '#76561A',
      goldDeep: '#654915',
      goldMuted: '#9E7A3E',
      goldSoft: 'rgba(166,124,46,0.16)',
      goldBorder: 'rgba(166,124,46,0.45)',
      freeze: '#5C8FB8',
      freezeMuted: 'rgba(92,143,184,0.55)',
      blue: '#3E74B8',
      blueSoft: '#6F9BD1',
      blueDeep: '#244E8C',
      blueMuted: '#4A6E9C',
      mint: '#1B7054',
      mintDeep: '#1F7A5C',
      mintSoft: '#7FC9AC',
      limeSoft: '#4F6B1F',
      glowBright: '#5C8A68',
      olive: '#8E8F4A',
      bronze: '#A86E3F',
      orange: '#C8641E',
      purple: '#6A5A8A',
    },
    chart: {
      pink: '#C96A9C',
      peach: '#D07A5A',
      salmon: '#C4836E',
      sand: '#C9A64A',
      sky: '#5F8FC0',
    },
    status: {
      danger: '#A23E32',
      dangerStrong: '#8A3128',
      dangerText: '#8F3A30',
      dangerTextSoft: '#954A40',
      dangerBg: '#F0D9D4',
      dangerBgMuted: '#E9CFC8',
      errorText: '#A23E32',
      errorTextStrong: '#8F2F24',
      errorTextSoft: '#B8665C',
      warning: '#8C5A12',
      warningText: '#7A4F0E',
      warningBorder: '#C48A2E',
      warningBg: '#F2E4C4',
      success: '#3D6A4B',
      successBg: '#E3ECC0',
      successStrong: '#2F7A45',
      dangerVivid: '#C0392B',
      dangerBgStrong: '#E6C2BC',
      dangerPanel: '#F3E1DD',
      errorTextMuted: '#B27068',
      warningStrong: '#B26A10',
      warningBorderMuted: '#C9A66B',
    },
    // The site's buttons: an ink fill with cream text, and a sand one with ink text.
    button: {
      primaryBg: '#1F2D24',
      primaryBorder: '#1F2D24',
      primaryText: '#F4F1EA',
      secondaryBg: '#E9E7DF',
      secondaryBorder: '#E9E7DF',
      secondaryText: '#1F2D24',
      ghostBg: 'transparent',
      ghostBorder: '#B8BEB7',
      ghostText: '#56625B',
    },
  },
  fonts: {
    brand: 'Archivo',
    mono: 'SpaceMono',
    webSerif: 'Archivo, "Helvetica Neue", Arial, sans-serif',
    // The site sets no serif anywhere, so the platform fallback is its sans too.
    systemSerif: 'sans-serif',
    systemMono: 'monospace',
  },
  // The site's .g-btn: a flat fill, no border, a small corner and a sentence-case label.
  button: {
    radius: 4,
    borderWidth: 0,
    label: { fontSize: 15, fontWeight: '600', letterSpacing: 0, textTransform: 'none' },
  },
  // The painted planks and animals belong to classic's dark green; on this light ground the
  // score input draws its controls from the palette, as yardage-book does.
  chrome: {
    scoreInput: 'drawn',
  },
  map: classicSkin.map,
};
